import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Download, Award, Lock, LogOut, Bell, ShieldAlert, AlertTriangle, ExternalLink, Sun, Moon, Monitor } from "lucide-react";
import axios from "axios";
import { ADMIN_TAB_GROUPS, ADMIN_STANDALONE_TAB } from "./adminTabsConfig";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Build a flat id -> label map once
const LABELS = (() => {
  const m = { [ADMIN_STANDALONE_TAB.id]: ADMIN_STANDALONE_TAB.label };
  ADMIN_TAB_GROUPS.forEach(g => g.items.forEach(it => { m[it.id] = it.label.replace(/^🎓\s*/, ""); }));
  m.sales_dashboard = "My Leads";
  m.reference_gallery = "Reference Gallery";
  return m;
})();

const NotificationBell = ({ onOpenDoozybot }) => {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchAlerts = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/doozybot/alerts`, { timeout: 8000 });
        if (!mounted) return;
        setAlerts((data.alerts || []).filter(a => a.severity === "high" || a.severity === "medium"));
      } catch { /* silent */ }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const highCount = alerts.filter(a => a.severity === "high").length;

  const handleAlertClick = (a) => {
    setOpen(false);
    if (a.action_link && a.action_link.startsWith("/")) window.location.href = a.action_link;
    else if (onOpenDoozybot) onOpenDoozybot();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-lg text-slate-500 hover:text-[#1E1B4B] hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DC283C]"
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={18} />
        {alerts.length > 0 && (
          <span className={`absolute top-1 right-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white ${highCount > 0 ? "bg-[#EF4444]" : "bg-[#F59E0B]"}`} data-testid="notification-count">
            {alerts.length > 9 ? "9+" : alerts.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white rounded-xl border border-slate-200 shadow-xl z-50" data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-sm font-bold text-[#1E1B4B]">Notifications</p>
            <span className="text-xs text-slate-400">{alerts.length} active</span>
          </div>
          {alerts.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Bell size={28} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">You're all caught up.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {alerts.map((a, i) => {
                const high = a.severity === "high";
                return (
                  <li key={a.id || `${a.title}-${a.detail}-${i}`}>
                    <button onClick={() => handleAlertClick(a)} className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex gap-3" data-testid={`notification-item-${i}`}>
                      <span className={`mt-0.5 ${high ? "text-[#EF4444]" : "text-[#F59E0B]"}`}><AlertTriangle size={16} /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-[#1E1B4B] truncate">{a.title}</span>
                        <span className="block text-xs text-slate-500 mt-0.5 line-clamp-2">{a.detail}</span>
                      </span>
                      <ExternalLink size={13} className="text-slate-300 flex-shrink-0 mt-0.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button onClick={() => { setOpen(false); onOpenDoozybot && onOpenDoozybot(); }} className="w-full px-4 py-2.5 text-xs font-semibold text-[#DC283C] hover:bg-[#FAD8E7]/40 border-t border-slate-100 transition-colors" data-testid="notification-open-doozybot">
            Open DoozyBot →
          </button>
        </div>
      )}
    </div>
  );
};

const ThemeToggle = ({ theme, setTheme }) => {
  const opts = [
    { v: "light", icon: Sun, label: "Light" },
    { v: "system", icon: Monitor, label: "System" },
    { v: "dark", icon: Moon, label: "Dark" },
  ];
  return (
    <div className="hidden sm:flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-100 border border-slate-200" data-testid="theme-toggle">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = theme === o.v;
        return (
          <button
            key={o.v}
            onClick={() => setTheme(o.v)}
            title={`${o.label} theme`}
            aria-pressed={active}
            data-testid={`theme-${o.v}`}
            className={`p-1.5 rounded-md transition-colors ${
              active ? "bg-white text-[#DC283C] shadow-sm" : "text-slate-400 hover:text-slate-700"
            }`}
          >
            <Icon size={14} />
          </button>
        );
      })}
    </div>
  );
};

const AdminHeader = ({ auth, onExportCSV, onOpenIncentives, onChangePassword, onLogout, allowedTabs, activeTab, onOpenDoozybot, theme, setTheme }) => {
  const title = LABELS[activeTab] || "Command Center";
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40" data-testid="admin-header">
      <div className="px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0" title="Back to Home" data-testid="admin-home-link">
            <Home size={16} className="text-slate-400" />
          </Link>
          <div className="h-5 w-px bg-slate-200 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-base font-bold text-[#1E1B4B] truncate leading-tight" data-testid="admin-page-title">{title}</h1>
            <p className="text-[11px] text-slate-400 leading-tight hidden sm:block">DoozyGo Command Center</p>
          </div>
          <span className="hidden md:inline-flex items-center gap-1 ml-1 px-2.5 py-0.5 rounded-full bg-[#F0DC00] text-[#b01e2f] border border-[#e4c900] text-[10px] font-extrabold uppercase tracking-wide flex-shrink-0 shadow-sm" title="Internal use only — do not display in front of outsiders" data-testid="confidential-chip">
            <ShieldAlert size={11} /> Confidential
          </span>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <div className="h-5 w-px bg-slate-200 mx-0.5 hidden sm:block" />
          <NotificationBell onOpenDoozybot={onOpenDoozybot} />
          <div className="h-5 w-px bg-slate-200 mx-0.5 hidden sm:block" />
          {allowedTabs.includes("calendar") && auth.role !== "security_manager" && auth.role !== "sales_executive" && (
            <button onClick={onExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-[#1E1B4B] rounded-lg text-xs font-semibold hover:bg-slate-50 transition-colors"
              data-testid="export-csv-btn">
              <Download size={14} />
              <span className="hidden sm:inline">Export</span>
            </button>
          )}
          {auth.role === "admin" && (
            <button onClick={onOpenIncentives}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9F1C] text-white rounded-lg text-xs font-semibold hover:brightness-95 transition-[filter]"
              data-testid="incentives-btn">
              <Award size={14} />
              <span className="hidden sm:inline">Incentives</span>
            </button>
          )}
          {onChangePassword && (
            <button onClick={onChangePassword}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-slate-500 hover:text-[#1E1B4B] hover:bg-slate-100 rounded-lg transition-colors text-xs font-medium"
              data-testid="change-password-btn">
              <Lock size={14} />
              <span>Password</span>
            </button>
          )}
          <button onClick={onLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 text-slate-500 hover:text-[#DC283C] hover:bg-slate-100 rounded-lg transition-colors text-xs font-medium"
            data-testid="admin-logout-btn">
            <LogOut size={14} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
